/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

/* Import API functions */
import { requestTimeSeriesDataset } from '@/api/eeCheckApi';

/* Import context */
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import {
    getTimeSeriesSensorNotFoundMessage,
    getTimeSeriesSensorNotNumericMessage,
} from '@/lib/sensors/presentation/sensorRequestMessages';

/* Import libraries */
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import {
    collapseItems,
    createErrorItems,
    createResolvedConfigs,
    normalizeCalcValue,
    type Api2ValueDataFlatItem,
    type Api2ValueDataItem,
    type Api2ValueDataRenderState,
    type Api2ValueDataResolvedConfig,
    type Api2ValueDataValue,
    type MaybeArray,
    type MaybeNestedArray,
} from '@/lib/overview/api2ValueData';
import {
    aggregateTimeSeriesValues,
    createTimeSeriesCalcValuesWithAliases,
    findTimeSeriesValueColumn,
    formatTimeSeriesNumericValue,
    getTimeSeriesAggregationDate,
    normalizeTimeSeriesUrn,
    type TimeSeriesCalc,
    type TimeSeriesAggregation,
} from '@/lib/timeSeries/timeSeriesValue';

/* Import types */
import type { TimeSeriesRequest } from '@/types/eeCheck';

export type Api2TimeSeriesDataItem = Api2ValueDataItem;
export type Api2TimeSeriesDataRenderState = Api2ValueDataRenderState;

interface Api2TimeSeriesDataProps {
    aggregation: MaybeNestedArray<TimeSeriesAggregation>;
    calc?: MaybeArray<TimeSeriesCalc | undefined>;
    children: (state: Api2TimeSeriesDataRenderState) => ReactNode;
    conversion?: MaybeNestedArray<number>;
    from: string;
    fractionDigits?: MaybeNestedArray<number>;
    offset?: MaybeNestedArray<number>;
    series?: MaybeNestedArray<string | undefined>;
    showDate?: boolean;
    title?: MaybeNestedArray<string | null | undefined>;
    to: string;
    unit?: MaybeNestedArray<string | null | undefined>;
    urn: string | string[] | string[][];
}

function Api2TimeSeriesData({
    aggregation,
    calc,
    children,
    conversion,
    from,
    fractionDigits,
    offset,
    series,
    showDate = false,
    title,
    to,
    unit,
    urn,
}: Api2TimeSeriesDataProps) {
    const { locale, t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [items, setItems] = useState<Api2TimeSeriesDataItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');

    const resolvedConfigs = useMemo(() => {
        return createResolvedConfigs(
            aggregation,
            conversion,
            fractionDigits,
            offset,
            series,
            title,
            unit,
            urn,
        );
    }, [aggregation, conversion, fractionDigits, offset, series, title, unit, urn]);
    const configsSignature = JSON.stringify(resolvedConfigs);

    useEffect(() => {
        if (isSessionExpired) {
            setIsLoading(false);
            return;
        }

        if (!accessToken) {
            setItems([]);
            setIsLoading(false);
            return;
        }

        if (resolvedConfigs.length === 0 || !from || !to) {
            setItems(
                createErrorItems(
                    resolvedConfigs,
                    t('Missing time series request parameters.'),
                ),
            );
            setIsLoading(false);
            return;
        }

        const requestUrn = Array.from(
            new Set(
                resolvedConfigs
                    .map((config) => normalizeTimeSeriesUrn(config.urn))
                    .filter(Boolean),
            ),
        ).join(',');
        const payload: TimeSeriesRequest = {
            from,
            to,
            urn: requestUrn,
        };

        let isCancelled = false;

        async function loadItems(): Promise<void> {
            setIsLoading(true);
            setLoadingLabel(t('Loading API request...'));

            try {
                const { data } = await requestTimeSeriesDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                if (typeof data !== 'string') {
                    setItems(
                        createErrorItems(
                            resolvedConfigs,
                            t('Time series response is not a CSV string.'),
                        ),
                    );
                    return;
                }

                const parsedTimeSeries = parseTimeSeriesCsv(data);
                const nextItems = resolvedConfigs.map((config) => {
                    const valueColumn = findTimeSeriesValueColumn(
                        parsedTimeSeries,
                        config.series ?? config.urn,
                    );

                    if (!valueColumn) {
                        return {
                            aggregation: config.aggregation,
                            date: null,
                            errorMessage: getTimeSeriesSensorNotFoundMessage(
                                t,
                                config.series ?? config.urn,
                            ),
                            formattedValue: null,
                            fractionDigits: config.fractionDigits,
                            groupIndex: config.groupIndex,
                            key: config.key,
                            rawValue: null,
                            title: config.title,
                            unit: config.unit,
                            urn: config.urn,
                            value: null,
                        };
                    }

                    const values = parsedTimeSeries.plotYAxisValues[valueColumn] ?? [];
                    const aggregatedValue = aggregateTimeSeriesValues(
                        values,
                        config.aggregation,
                    );

                    if (aggregatedValue === null) {
                        return {
                            aggregation: config.aggregation,
                            date: null,
                            errorMessage: getTimeSeriesSensorNotNumericMessage(
                                t,
                                config.series ?? config.urn,
                            ),
                            formattedValue: null,
                            fractionDigits: config.fractionDigits,
                            groupIndex: config.groupIndex,
                            key: config.key,
                            rawValue: null,
                            title: config.title,
                            unit: config.unit,
                            urn: config.urn,
                            value: null,
                        };
                    }

                    const convertedValue =
                        aggregatedValue * config.conversion + config.offset;

                    return {
                        aggregation: config.aggregation,
                        date: showDate
                            ? getTimeSeriesAggregationDate(
                                values,
                                parsedTimeSeries.plotXAxisValues,
                                config.aggregation,
                            )
                            : null,
                        errorMessage: '',
                        formattedValue: formatTimeSeriesNumericValue(
                            convertedValue,
                            locale,
                            config.fractionDigits,
                        ),
                        fractionDigits: config.fractionDigits,
                        groupIndex: config.groupIndex,
                        key: config.key,
                        rawValue: aggregatedValue,
                        title: config.title,
                        unit: config.unit,
                        urn: config.urn,
                        value: convertedValue,
                    };
                });

                const computedItems = new Map<number, Api2ValueDataFlatItem[]>();

                nextItems.forEach((item) => {
                    const currentGroupItems = computedItems.get(item.groupIndex) ?? [];
                    currentGroupItems.push(item);
                    computedItems.set(item.groupIndex, currentGroupItems);
                });

                computedItems.forEach((groupItems, groupIndex) => {
                    const calcForGroup = normalizeCalcValue(calc, groupIndex);

                    if (!calcForGroup) {
                        return;
                    }

                    const values = groupItems.reduce<Record<string, number | null>>((result, item) => {
                        result[item.urn] = item.rawValue;
                        return result;
                    }, {});
                    const computedValues = calcForGroup({
                        values: createTimeSeriesCalcValuesWithAliases(values),
                    });
                    const firstGroupItem = groupItems[0];
                    const fallbackDate = groupItems.find((item) => item.date !== null)?.date ?? null;

                    Object.entries(computedValues).forEach(([computedKey, computedValue], computedIndex) => {
                        const formattedValue = computedValue === null
                            ? null
                            : formatTimeSeriesNumericValue(
                                computedValue,
                                locale,
                                firstGroupItem?.fractionDigits,
                            );

                        groupItems.push({
                            aggregation: firstGroupItem?.aggregation ?? 'last',
                            date: fallbackDate,
                            errorMessage: '',
                            formattedValue,
                            fractionDigits: firstGroupItem?.fractionDigits ?? 2,
                            groupIndex,
                            key: `${computedKey}-${groupIndex}-calc-${computedIndex}`,
                            rawValue: computedValue,
                            title: firstGroupItem?.title ?? null,
                            unit: null,
                            urn: computedKey,
                            value: computedValue,
                        });
                    });
                });

                const nextItemsWithComputedValues = Array.from(computedItems.values()).flat();

                setItems(collapseItems(nextItemsWithComputedValues));
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                setItems(
                    createErrorItems(
                        resolvedConfigs,
                        error instanceof Error
                            ? error.message
                            : t('Time series request failed.'),
                    ),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                    setLoadingLabel('');
                }
            }
        }

        void loadItems();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, calc, configsSignature, from, isSessionExpired, locale, onSessionExpired, resolvedConfigs, showDate, t, to]);

    return <>{children({ items, isLoading, loadingLabel })}</>;
}

export default Api2TimeSeriesData;
